import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { makeAutoObservable } from 'mobx';

import Step1 from './Step1';

import styles from '../AboutCompany/styles.module.scss';
import { authorizedRequest } from 'utils/authorization';
import { getToken } from 'utils/token';
import axios from 'axios';
import userStore from 'store/User';
import appStore from 'store/App';
import { about } from '../AboutCompany';
import Auth from 'store/Auth';

type CompanySignatory = {
  alreadyCreated: boolean;
  fullName?: string;
  first_name?: string;
  last_name?: string;
  position: string;
  email: string;
  id: string;
  companySignatoryFile: any;
};

class CompanySignatoryBC {
  constructor() {
    makeAutoObservable(this);
  }

  values: CompanySignatory = {
    alreadyCreated: false,
    fullName: '',
    first_name: '',
    last_name: '',
    position: '',
    email: '',
    id: '',
    companySignatoryFile: {
      name: '',
      value: '',
      size: 0,
    },
  };

  isStepIsValid = {
    1: false,
  };

  isDataSended = false;
  //@ts-ignore
  init(contact) {
    if (contact && contact?.full_name) {
      this.values.fullName = contact.full_name;
      this.values.first_name = contact.first_name;
      this.values.last_name = contact.last_name;
      this.values.position = contact.position;
      this.values.email = contact.email;
      this.values.id = contact.id;
      this.values.alreadyCreated = true;
    }

    // Всегда устанавливаем isDataSended на основе серверных данных (независимо от наличия контакта)
    // Не сбрасываем isDataSended если данные уже были отправлены локально
    if (!this.isDataSended) {
      this.isDataSended = userStore.onboardingSteps?.companySignatory ?? false;
    }
  }

  // Метод для загрузки существующих документов
  async loadExistingDocuments() {
    try {
      const companyId = userStore.me?.company?.id.toString() || '';
      if (!companyId) return;

      const result = await authorizedRequest({
        url: `/v1/documents/company/${companyId}`,
        method: 'GET',
        service: 'fca',
        data: {},
      });

      console.log('-->', result);

      // Ищем документ типа "signatory-confirming-document"
      const signatoryDoc = result.find(
        (doc: any) => doc.doc_type === 'signatory-confirming-document',
      );

      if (signatoryDoc && signatoryDoc.file) {
        this.values.companySignatoryFile = {
          id: signatoryDoc.file.id,
          docId: signatoryDoc.id,
          name: signatoryDoc.file.name,
          size: 1, // Указываем что файл загружен
          type: signatoryDoc.file.media_type,
          value: { type: signatoryDoc.file.media_type }, // Для корректного отображения иконки
        };
      }
    } catch (error) {
      console.error('Failed to load existing documents:', error);
    }
  }

  async sendFile() {
    try {
      // work with file -->
      if (this.values.companySignatoryFile.size > 0) {
        if (this.values.companySignatoryFile?.docId) {
          await authorizedRequest({
            url: `/v1/documents/${this.values.companySignatoryFile?.docId}`,
            method: 'DELETE',
            service: 'fca',
            data: {},
          });
        }

        let data = new FormData();

        if (this.values.companySignatoryFile.value.size > 0) {
          data.append(
            'file',
            this.values.companySignatoryFile.value,
            this.values.companySignatoryFile.name,
          );
        } else {
          data.append('file', '');
        }

        let config = {
          method: 'post',
          maxBodyLength: Infinity,
          url: `${process.env.REACT_APP_SCA_URL}/v1/documents/?doc_type=signatory-confirming-document`,
          headers: {
            accept: 'application/json',
            authorization: `Bearer ${Auth.scaToken}`,
          },
          data,
        };

        const result = await axios.request(config);

        // интерцептор резолвит ошибку вместо reject
        if (axios.isAxiosError(result)) throw result;

        this.values.companySignatoryFile.id = result.data?.file?.id;
        this.values.companySignatoryFile.docId = result.data?.id;
        this.values.companySignatoryFile.name = result.data?.file?.name;
        this.values.companySignatoryFile.size = 1;
      }
      // work with file <--

      // work with contact -->
      const setContact = (data: any) => {
        if (!data) return;

        const contact = {
          id: data?.id,
          type: data?.type,
          contact_phone: data?.contact_phone,
          email: data?.email,
          full_name: data?.full_name,
          first_name: data?.first_name,
          last_name: data?.last_name,
          position: data?.position,
        };
        const index = about.values.contacts.findIndex(
          (item) => item.type === data?.type,
        );

        if (index === -1) {
          about.values.contacts.push(contact);
        } else {
          about.values.contacts[index] = contact;
        }
      };

      // contact_phone не отправляем: поля телефона нет в форме подписанта,
      // а пустую строку бэкенд отклоняет по регулярке
      const signerData = {
        type: 'signer',
        full_name: this.values.first_name! + this.values.last_name!,
        first_name: this.values.first_name,
        last_name: this.values.last_name,
        position: this.values.position,
        email: this.values.email,
      };

      let isContactSaved = false;
      try {
        const updateResult = await authorizedRequest({
          url: this.values.alreadyCreated
            ? `/v1/companies/${userStore.me?.company?.id.toString()}/contacts/${
                this.values.id
              }`
            : `/v1/companies/${userStore.me?.company?.id.toString()}/contacts`,
          service: 'fca',
          method: this.values.alreadyCreated ? 'PATCH' : 'POST',
          data: signerData,
        });
        setContact(updateResult);
        isContactSaved = true;
      } catch (e) {
        console.error('CompanySignatory contact error:', e);
      }
      // work with contact <--

      if (!isContactSaved) {
        appStore.setNotification({
          type: 'error',
          title: 'Company Signatory information was not saved',
          message: 'Please try once again',
          width: 380,
        });

        return false;
      }

      // Обновляем статус онбординга в userStore
      await userStore.getOnboarding(true);

      appStore.setNotification({
        type: 'success',
        title: `Company Signatory information was sent successful`,
        width: 380,
      });

      return true;
    } catch (error) {
      console.error('CompanySignatory sendFile error:', error);
      appStore.setNotification({
        type: 'error',
        title: 'Ошибка отправки данных',
        message: 'Попробуйте еще раз',
        width: 380,
      });

      return false;
    }
  }
}

export const companySignatoryInst = new CompanySignatoryBC();

const CompanySignatory = observer(() => {
  // Синхронизируем данные при открытии компонента
  // loadExistingDocuments уже вызван в App.tsx, просто копируем данные в локальное состояние
  useEffect(() => {
    const signer = about.values.contacts.find((item) => item.type === 'signer');
    companySignatoryInst.init(signer || null);
  }, []);

  return (
    <div className={styles.container}>
      <Step1 />
    </div>
  );
});

export default CompanySignatory;
